<script setup>
// ============================================================================
// BonBoZ: menu dọc bên trái BÊN TRONG một trang (Cài đặt, Báo cáo).
//
// Một mục phải qua hai cửa: `router.hasRoute()` để không ném lỗi lúc dựng
// liên kết, và `usePolicy().shouldShow()` — đúng hàm Chatwoot dùng để gác các
// route đó, nếu không thì menu hiện mục bấm vào bị chặn.
//
// Mục có `nhom` là tiêu đề nhóm; lọc xong không còn mục nào bên dưới thì bỏ.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { locMucHienDuoc } from 'dashboard/helper/subPageMenu';

const props = defineProps({
  muc: { type: Array, required: true },
  // BonBoZ: Pancake de tieu de trang ngay tren menu (reference §1
  // `.title-page-setting`). Khoa dich, de trong thi khong hien - trang Bao cao
  // dung chung component nay va chua co tieu de.
  tieuDe: { type: String, default: '' },
});

const route = useRoute();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const { shouldShow } = usePolicy();

const metaCua = ten => router.getRoutes().find(r => r.name === ten)?.meta ?? {};

const hienDuoc = m => {
  if (!router.hasRoute(m.toi)) return false;
  const { featureFlag, permissions, installationTypes } = metaCua(m.toi);
  return shouldShow(featureFlag, permissions, installationTypes);
};

const cacMuc = computed(() =>
  locMucHienDuoc(props.muc, hienDuoc).map(m =>
    m.nhom
      ? m
      : {
          ...m,
          toiDay: { name: m.toi, params: { accountId: accountId.value } },
        }
  )
);

// Muc dang mo to xanh Pancake `--pk-muc-chon-*` thay cho teal cua BonBoZ:
// doi truc man Cai dat sang dung khuon Pancake (reference §1).
const dangO = m => route.name === m.toi || String(route.name).startsWith(m.toi);

const thanhMenu = ref(null);

// O dai ngang tren dien thoai, muc dang mo thuong nam ngoai tam nhin ngay khi
// vao trang: nguoi dung thay mot day ten la va khong biet minh dang o dau. Keo
// no vao giua. Tren may tinh menu doc khong bao gio tran ngang nen dieu kien
// duoi day tu bo qua, khong can hoi be ngang man hinh.
const keoMucDangMoVaoTam = () => {
  // Doi them mot khung hinh sau khi DOM da vao: `scrollWidth` chi dung sau khi
  // trinh duyet do xong be ngang cac muc. Chay som hon thi dai menu con rong
  // bang khung, dieu kien duoi day tuong la khong can cuon va bo qua luon.
  requestAnimationFrame(() => {
    const thanh = thanhMenu.value;
    if (!thanh || thanh.scrollWidth <= thanh.clientWidth) return;
    const muc = thanh.querySelector('[data-dang-o="true"]');
    // `block: 'nearest'` de khong keo ca trang truot doc theo.
    if (muc) muc.scrollIntoView({ inline: 'center', block: 'nearest' });
  });
};

// Theo doi CA danh sach muc, khong chi ten route: luc tai trang lan dau menu
// duoc dung sau khi quyen va route da nap xong, doi mot minh `route.name` thi
// lan chay duy nhat roi vao thoi diem dai menu con rong.
watch([() => route.name, cacMuc], keoMucDangMoVaoTam, {
  immediate: true,
  flush: 'post',
});
</script>

<template>
  <!-- BonBoZ: so do lay tu CSS that cua Pancake (reference §1) - cot 280px,
       muc cao 42px bo goc 6px, chu 14/24. Mau lay qua bien `--pk-*`.

       Duoi 768px doi thanh DAI NGANG cuon duoc: cot 280px chiem gan het man
       dien thoai, khong con cho cho noi dung. Tieu de trang va tieu de nhom an
       o dai ngang — chung an theo chieu doc, ma moi trang ben trong da co tieu
       de rieng (`BaseSettingsHeader` / `ReportHeader`). -->
  <nav
    ref="thanhMenu"
    class="flex shrink-0 bg-[rgb(var(--pk-the-nen))] border-[rgb(var(--pk-vach))] flex-row w-full overflow-x-auto border-b md:flex-col md:w-[280px] md:overflow-x-hidden md:overflow-y-auto md:border-b-0 md:border-e"
  >
    <div
      v-if="tieuDe"
      class="hidden md:block mx-6 my-4 text-2xl leading-8 font-medium text-[rgb(var(--pk-chu-chinh))]"
    >
      {{ $t(tieuDe) }}
    </div>
    <template v-for="m in cacMuc" :key="m.nhom || m.toi">
      <div
        v-if="m.nhom"
        class="hidden md:block mx-4 px-4 pt-4 pb-1 text-xs font-medium uppercase tracking-wide text-[rgb(var(--pk-chu-mo))]"
      >
        {{ $t(m.nhom) }}
      </div>
      <RouterLink
        v-else
        :to="m.toiDay"
        :data-dang-o="dangO(m)"
        class="flex items-center shrink-0 mx-1 my-2 md:mx-4 md:my-1 h-[42px] px-4 rounded-md text-sm leading-6 select-none transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-n-brand"
        :class="
          dangO(m)
            ? 'bg-[rgb(var(--pk-muc-chon-nen))] text-[rgb(var(--pk-muc-chon-chu))] font-medium'
            : 'text-[rgb(var(--pk-chu-chinh))] hover:bg-[rgb(var(--pk-hover))]'
        "
      >
        <span :class="m.icon" class="size-5 shrink-0 mr-3" aria-hidden="true" />
        <span class="truncate">{{ $t(m.nhan) }}</span>
      </RouterLink>
    </template>
  </nav>
</template>
